import i18n from 'i18next';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Building2, Pencil, Plus, UserMinus } from 'lucide-react';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { Modal } from '../../componentes/sobreposicao';
import { TextoCortado } from '../../componentes/texto-cortado';
import { useToast } from '../../componentes/toast';
import { get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { data, dinheiro, hojeIso, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { campoEmpresaDoRegistro, empresaEscolhida } from '../../nucleo/empresa/empresa-do-registro';
import { COR_CONTRATO, COR_ESTADO, CONTRATOS, limpar, type Ficha, type Vinculo } from './tipos';

/** Campos do vínculo (contrato, datas, salário quando a pessoa vê, departamento e função): o mesmo conjunto da admissão. */
export function useCamposVinculo(): DefCampo[] {
  const { t } = useTranslation('pessoas');
  const verSalario = useSessao().pode('pessoas.confidencial');
  return [
    { nome: 'contrato_tipo', rotulo: t('campos.contrato'), tipo: 'selecao', obrigatorio: true, largura: 'terco', opcoes: CONTRATOS.map((c) => ({ valor: c, rotulo: t(`contrato.${c}`) })) },
    { nome: 'admissao', rotulo: t('campos.admissao'), tipo: 'data', obrigatorio: true, largura: 'terco' },
    { nome: 'fim_contrato', rotulo: t('campos.fimContrato'), tipo: 'data', largura: 'terco' },
    ...(verSalario ? ([{ nome: 'salario_centavos', rotulo: t('campos.salario'), tipo: 'dinheiro', largura: 'meia' }] as DefCampo[]) : []),
    { nome: 'departamento_id', rotulo: t('campos.departamento'), tipo: 'busca', largura: 'meia', busca: { caminho: '/pessoas/departamentos', rotulo: (i) => String(i.nome) } },
    { nome: 'funcao_id', rotulo: t('campos.funcao'), tipo: 'busca', largura: 'meia', busca: { caminho: '/pessoas/funcoes', rotulo: (i) => String(i.nome) } },
  ];
}

/** Corpo do vínculo a partir dos valores do formulário (o salário só vai com a permissão de vê-lo). */
export function corpoVinculo(v: Record<string, unknown>, verSalario: boolean) {
  return limpar({
    contrato_tipo: v.contrato_tipo,
    admissao: v.admissao,
    fim_contrato: v.fim_contrato,
    ...(verSalario ? { salario_centavos: v.salario_centavos ?? null } : {}),
    departamento_id: v.departamento_id,
    funcao_id: v.funcao_id,
  });
}

/**
 * Fase 2 (W16; pendência da W2b, resposta 1 do Gustavo): um vínculo por empresa do grupo, cada um com admissão, função,
 * departamento, salário, contrato e situação. Só aparecem os vínculos das empresas que a pessoa vê. O principal (espelho
 * da ficha) muda pela ficha; os outros se editam e se encerram aqui. "Acrescentar vínculo nesta empresa" cria o da empresa
 * atual (um em aberto por empresa).
 */
export function VinculosCard({ ficha }: { ficha: Ficha }) {
  const { t } = useTranslation('pessoas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const campos = useCamposVinculo();
  const verSalario = s.pode('pessoas.confidencial');
  const q = useQuery({ queryKey: ['pessoas', 'vinculos', ficha.id], queryFn: () => get<{ itens: Vinculo[] }>(`/pessoas/funcionarios/${ficha.id}/vinculos`) });
  const [form, setForm] = useState<Vinculo | 'novo' | null>(null);
  const [encerrar, setEncerrar] = useState<Vinculo | null>(null);
  const recarregar = async () => {
    await qc.invalidateQueries({ queryKey: ['pessoas'] });
    await qc.invalidateQueries({ queryKey: ['/pessoas/funcionarios'] });
  };
  const encerrarVinculo = useMutation({
    mutationFn: (v: { id: string; data?: string }) => post(`/pessoas/vinculos/${v.id}/encerrar`, limpar({ data: v.data })),
    onSuccess: async () => {
      setEncerrar(null);
      await recarregar();
      toast.sucesso(tn('comum.salvo'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const atual = form && form !== 'novo' ? form : null;
  const itens = q.data?.itens ?? [];
  // Em "Todas as empresas" não há empresa atual: o botão fica e a API recusa o repetido (409 vinculo.ja_existe).
  const aqui = s.empresas?.atual ?? null;
  const temAbertoAqui = !!aqui && itens.some((v) => v.estado !== 'desligado' && v.empresa_id === aqui);

  return (
    <Cartao
      titulo={t('vinculos.titulo')}
      acao={
        s.pode('pessoas.criar') &&
        !temAbertoAqui && (
          // Rótulo curto no cabeçalho do cartão (como em Documentos), o completo para o leitor de tela e no título do diálogo.
          <Botao variante="fantasma" tamanho="sm" icone={<Plus className="size-4" />} aria-label={t('vinculos.acrescentar')} title={t('vinculos.acrescentar')} onClick={() => setForm('novo')}>
            {t('acoes.adicionar')}
          </Botao>
        )
      }
    >
      <p className="mb-2 text-xs text-tinta-3">{t('vinculos.ajuda')}</p>
      {q.isPending ? (
        <Carregando />
      ) : q.isError ? (
        <Erro erro={q.error} aoTentar={() => q.refetch()} />
      ) : itens.length === 0 ? (
        <Vazio icone={<Building2 className="size-5" aria-hidden />} texto={t('vinculos.vazio')} />
      ) : (
        <ul className="divide-y divide-borda" data-vinculos="">
          {itens.map((v) => (
            <li key={v.id} className="flex min-w-0 flex-wrap items-start justify-between gap-x-4 gap-y-1 py-2 text-sm">
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="flex min-w-0 flex-wrap items-center gap-2">
                  <TextoCortado className="min-w-0 font-medium">{textoLivre(v.empresa_nome)}</TextoCortado>
                  {v.principal && (
                    <Selo cor="acento">{t('vinculos.principal')}</Selo>
                  )}
                  <Selo cor={COR_ESTADO[v.estado]}>{t(`estado.${v.estado}`)}</Selo>
                  <Selo cor={COR_CONTRATO[v.contrato_tipo]}>{t(`contrato.${v.contrato_tipo}`)}</Selo>
                </span>
                <span className="text-tinta-2">{[v.funcao_nome, v.departamento_nome].filter(Boolean).join(' / ') || t('ficha.semFuncao')}</span>
                <span className="numero text-xs text-tinta-3">
                  {v.desligamento ? i18n.t('comum.intervalo', { de: data(v.admissao), ate: data(v.desligamento) }) : t('vinculos.desde', { data: data(v.admissao) })}
                </span>
                <span className="numero text-tinta-2">{v.salario_centavos !== null ? dinheiro(v.salario_centavos, v.moeda) : verSalario ? '-' : t('ficha.salarioOculto')}</span>
              </span>
              {!v.principal && v.estado !== 'desligado' && (
                <span className="flex items-center gap-1">
                  {s.pode('pessoas.editar') && (
                    <Botao variante="fantasma" tamanho="sm" icone={<Pencil className="size-4" />} onClick={() => setForm(v)}>
                      {tn('comum.editar')}
                    </Botao>
                  )}
                  {s.pode('pessoas.excluir') && (
                    <Botao variante="fantasma" tamanho="sm" icone={<UserMinus className="size-4" />} onClick={() => setEncerrar(v)}>
                      {t('vinculos.encerrar')}
                    </Botao>
                  )}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      <Modal
        aberto={!!form}
        aoFechar={() => setForm(null)}
        largura="lg"
        titulo={atual ? t('vinculos.editar') : t('vinculos.acrescentar')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setForm(null)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form="form-vinculo">
              {tn('comum.salvar')}
            </Botao>
          </>
        }
      >
        {form && (
          <Formulario
            key={atual?.id ?? 'novo'}
            id="form-vinculo"
            campos={atual ? campos : [...campoEmpresaDoRegistro(s.empresas, { rotulo: tn('empresa.rotulo'), ajuda: t('vinculos.empresaAjuda') }), ...campos]}
            inicial={atual ? { ...atual } : { contrato_tipo: 'clt', admissao: hojeIso() }}
            rotulosAtuais={atual ? { departamento_id: atual.departamento_nome ?? '', funcao_id: atual.funcao_nome ?? '' } : undefined}
            aoEnviar={async (v) => {
              const corpo = corpoVinculo(v, verSalario);
              if (atual) await patch(`/pessoas/vinculos/${atual.id}`, corpo);
              // Rodada rval1-1 (RVAL1-ROB-M2): sem empresa atual (troca desligada ou "Minhas verticais"), a do formulário.
              else await post(`/pessoas/funcionarios/${ficha.id}/vinculos`, { ...corpo, ...(empresaEscolhida(v) ? { empresa_id: empresaEscolhida(v) } : {}) });
              setForm(null);
              await recarregar();
              toast.sucesso(atual ? tn('comum.salvo') : t('vinculos.acrescentado'));
            }}
          />
        )}
      </Modal>

      <Modal
        aberto={!!encerrar}
        aoFechar={() => setEncerrar(null)}
        titulo={t('vinculos.encerrar')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setEncerrar(null)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="perigo" type="submit" form="form-encerrar-vinculo" carregando={encerrarVinculo.isPending}>
              {tn('comum.confirmar')}
            </Botao>
          </>
        }
      >
        <p className="mb-4 text-sm text-tinta-2">{t('vinculos.encerrarTexto')}</p>
        {encerrar && (
          <Formulario
            key={encerrar.id}
            id="form-encerrar-vinculo"
            campos={[{ nome: 'data', rotulo: t('campos.dataEfetiva'), tipo: 'data', largura: 'meia', ajuda: t('campos.dataEfetivaAjuda') }]}
            inicial={{}}
            aoEnviar={async (v) => encerrarVinculo.mutateAsync({ id: encerrar.id, data: (v.data as string) || undefined })}
          />
        )}
      </Modal>
    </Cartao>
  );
}
